import { SITE_LOCALES, type SiteLocale } from '@/lib/marketing/page-metadata';
import { INDUSTRY_SLUGS, type IndustrySlug } from '@/lib/marketing/industries/types';

// Kept free of page copy: middleware (edge) and client components import this,
// and lib/marketing/industries pulls every language's text with it.

export const DEFAULT_SITE_LOCALE = 'en' satisfies SiteLocale;

export type TranslatedLocale = Exclude<SiteLocale, typeof DEFAULT_SITE_LOCALE>;

export const TRANSLATED_LOCALES = SITE_LOCALES.filter(
  (l): l is TranslatedLocale => l !== DEFAULT_SITE_LOCALE,
);

const PATH_PREFIX: Record<SiteLocale, string> = { en: '', es: '/es', fr: '/fr' };

// The visitor's explicit choice (footer switcher, suggestion bar). It beats the
// browser's language everywhere: the home redirect and the suggestion bar.
export const LANG_COOKIE = 'apr_lang';
export const LANG_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function isSiteLocale(value: unknown): value is SiteLocale {
  return typeof value === 'string' && (SITE_LOCALES as readonly string[]).includes(value);
}

export function isIndustrySlug(value: string): value is IndustrySlug {
  return (INDUSTRY_SLUGS as readonly string[]).includes(value);
}

export function homePath(locale: SiteLocale): string {
  return PATH_PREFIX[locale] || '/';
}

export function industryPath(locale: SiteLocale, slug: IndustrySlug): string {
  return `${PATH_PREFIX[locale]}/for/${slug}`;
}

export function localeOfPath(pathname: string): SiteLocale {
  return (
    TRANSLATED_LOCALES.find(
      (l) => pathname === PATH_PREFIX[l] || pathname.startsWith(`${PATH_PREFIX[l]}/`),
    ) ?? DEFAULT_SITE_LOCALE
  );
}

/** The same page in `target`, or null when that page has no translation. */
export function translatedPath(pathname: string, target: SiteLocale): string | null {
  const from = localeOfPath(pathname);
  const base = (pathname.slice(PATH_PREFIX[from].length) || '/').replace(/(.)\/+$/, '$1');
  if (base === '/') return homePath(target);
  const industry = base.match(/^\/for\/([^/]+)$/);
  if (industry && isIndustrySlug(industry[1])) return industryPath(target, industry[1]);
  return null;
}

/** The site language a browser asks for first, from an Accept-Language
 *  header or `navigator.languages.join(',')`. Null when it asks for none. */
export function preferredSiteLocale(acceptLanguage: string | null | undefined): SiteLocale | null {
  if (!acceptLanguage) return null;
  const ranked = acceptLanguage
    .split(',')
    .map((part, index) => {
      const [tag, ...params] = part.trim().split(';');
      const q = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
      const weight = q ? Number(q.slice(2)) : 1;
      return {
        lang: tag.trim().toLowerCase().split('-')[0],
        weight: Number.isFinite(weight) ? weight : 0,
        index,
      };
    })
    .filter((r) => r.lang && r.weight > 0)
    .sort((a, b) => b.weight - a.weight || a.index - b.index);
  return ranked.map((r) => r.lang).find(isSiteLocale) ?? null;
}

/**
 * Where a request for the English home should go instead, or null to serve it.
 * Only `/`, only a page load, and only toward a translated home: Google advises
 * against language redirects, tolerates one on the home page, and crawls with
 * no Accept-Language — so search engines always get the English `/`.
 * `fetchDest` is the browser's Sec-Fetch-Dest: in-app navigation and prefetches
 * send `empty` (Next strips its own RSC headers before middleware runs, so this
 * is the only tell). Absent on old browsers and curl — treated as a page load.
 */
export function localizedHomeRedirect(req: {
  pathname: string;
  method: string;
  fetchDest: string | null;
  cookie: string | undefined;
  acceptLanguage: string | null;
}): string | null {
  if (req.pathname !== '/' || req.method !== 'GET') return null;
  if (req.fetchDest && req.fetchDest !== 'document') return null;
  const target = isSiteLocale(req.cookie) ? req.cookie : preferredSiteLocale(req.acceptLanguage);
  if (!target || target === DEFAULT_SITE_LOCALE) return null;
  return homePath(target);
}

export function readLangCookie(): SiteLocale | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LANG_COOKIE}=([^;]*)`));
  const value = match ? decodeURIComponent(match[1]) : null;
  return isSiteLocale(value) ? value : null;
}

export function writeLangCookie(locale: SiteLocale): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${LANG_COOKIE}=${locale}; Path=/; Max-Age=${LANG_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}
